import { useState } from 'react';
import { CircularProgress, Button, Grid, TextField, Alert, Autocomplete, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

import { DECIMAL_REGEX, CURRENCY_REGEX } from '../../utils/constants';
import axiosInstance from '../../api';
import SERVER_API_URL from '../../config';
import { REQUIRED_FIELD, INTERNAL_SERVER_ERROR, CURRENCY_EXCEPTION, DECIMAL_EXCEPTION } from '../../utils/exception';
import StyledCardNoHeight from '../../components/card/StyledCardNoHeight';

export default function AnnouncePayment({ user, bankAccounts }) {
  const [disabled, setDisabled] = useState(false);

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [accountClicked, setAccountClicked] = useState(false);
  const [formErrorOnAccount, setFormErrorOnAccount] = useState(false);
  const [helperTextOnAccount, setHelperTextOnAccount] = useState(null);

  const [amountClicked, setAmountClicked] = useState(false);
  const [formErrorOnAmount, setFormErrorOnAmount] = useState(false);
  const [helperTextOnAmount, setHelperTextOnAmount] = useState(null);

  const [currencyClicked, setCurrencyClicked] = useState(false);
  const [formErrorOnCurrency, setFormErrorOnCurrency] = useState(false);
  const [helperTextOnCurrency, setHelperTextOnCurrency] = useState(null);


  const handleSubmitNewPayment = async (event) => {
    event.preventDefault();
    setDisabled(true);

    if (!formErrorOnAccount && !formErrorOnAmount && !formErrorOnCurrency) {
      const { amount, currency } = event.target.elements;
      try {
        const response = await axiosInstance.post(`${SERVER_API_URL}/payment/announce/${user && user.id}`, {
          subaccount_id: selectedBankAccount.subaccount_id,
          amount: amount.value,
          currency: currency.value,
        });
        if (response.status === 201) {
          setFormError('ክፍያ በተሳካ ሁኔታ ተመዝግቧል።');
          setFormErrorSeverity('success');
        }
      } catch (e) {
        if (e.response.status === 406) {
          setFormError('የክፍያ ዓመት ልዩ መሆን አለበት!');
          setFormErrorSeverity('error');
        } else if (e.response.status === 400) {
          setFormError(e.response.data.message);
          setFormErrorSeverity('error');
        } else if (e.response.status === 500) {
          setFormError(INTERNAL_SERVER_ERROR);
          setFormErrorSeverity('error');
        }
      }
    } else {
      setFormError('እባክዎ ባዶ ቦታውን ይሙሉ!');
      setFormErrorSeverity('error');
    }
    setDisabled(false);
  };

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'account':
        if (value.length === 0) {
          setAccountClicked(false);
          setFormErrorOnAccount(accountClicked && value.length === 0);
          setHelperTextOnAccount(REQUIRED_FIELD);
        } else if (!bankAccounts.filter((account) => account.account_number === value)) {
          setFormErrorOnAccount(true);
          setHelperTextOnAccount("የባንክ አካውንት ከተዘረዘሩት ውስጥ መሆን አለበት።");
        } else {
          setHelperTextOnAccount(null);
          setFormErrorOnAccount(false);
        }
        break;
      case 'amount':
        if (value.length === 0) {
          setAmountClicked(false);
          setFormErrorOnAmount(amountClicked && value.length === 0);
          setHelperTextOnAmount(REQUIRED_FIELD);
        } else if (!DECIMAL_REGEX.test(value)) {
          setFormErrorOnAmount(true);
          setHelperTextOnAmount(DECIMAL_EXCEPTION);
        } else {
          setHelperTextOnAmount(null);
          setFormErrorOnAmount(false);
        }
        break;
      case 'currency':
        if (value.length === 0) {
          setCurrencyClicked(false);
          setFormErrorOnCurrency(currencyClicked && value.length === 0);
          setHelperTextOnCurrency(REQUIRED_FIELD);
        } else if (!CURRENCY_REGEX.test(value)) {
          setFormErrorOnCurrency(true);
          setHelperTextOnCurrency(CURRENCY_EXCEPTION);
        } else {
          setHelperTextOnCurrency(null);
          setFormErrorOnCurrency(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');
    const { name } = e.target;
    switch (name) {
      case 'account':
        setFormErrorOnAccount(false);
        setHelperTextOnAccount(null);
        break;
      case 'amount':
        setFormErrorOnAmount(false);
        setHelperTextOnAmount(null);
        break;
      case 'currency':
        setFormErrorOnCurrency(false);
        setHelperTextOnCurrency(null);
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    setFormError(null);
    setFormErrorSeverity('error');

    setAmountClicked(false);
    setFormErrorOnAmount(false);
    setHelperTextOnAmount(null);

    setCurrencyClicked(false);
    setFormErrorOnCurrency(false);
    setHelperTextOnCurrency(null);
  };

  return (
    <StyledCardNoHeight
      sx={{ padding: { xs: 1, sm: 5, md: 10 } }}
      component="form"
      autoComplete="off"
      onSubmit={handleSubmitNewPayment}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={8} md={6}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                ክፍያ ማሳውቂያ
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {formError && (
                <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
                  {formError}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                fullWidth
                options={bankAccounts}
                getOptionLabel={(option) => `${option.account_number} - ${option.bank_name} (${option.account_name})`}
                onChange={(event, newValue) => setSelectedBankAccount(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    error={formErrorOnAccount}
                    fullWidth
                    id="account"
                    name="account"
                    label="የባንክ አካውንት"
                    // variant="standard"
                    onChange={handleFormValueChange}
                    helperText={formErrorOnAccount ? helperTextOnAccount : null}
                    onFocus={() => setAccountClicked(true)}
                    onBlur={handleFormValueBlur}
                  />
                )}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                required
                error={formErrorOnAmount}
                fullWidth
                id="amount"
                name="amount"
                label="የብር መጠን"
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnAmount ? helperTextOnAmount : null}
                onFocus={() => setAmountClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                freeSolo
                fullWidth
                value="ETB"
                options={['ETB', 'USD']}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    error={formErrorOnCurrency}
                    fullWidth
                    id="currency"
                    name="currency"
                    label="ክረንሲ"
                    // variant="standard"
                    onChange={handleFormValueChange}
                    helperText={formErrorOnCurrency ? helperTextOnCurrency : null}
                    onFocus={() => setCurrencyClicked(true)}
                    onBlur={handleFormValueBlur}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right', m: 2 }}>
              <Button color="error" type="reset" onClick={handleClearForm} sx={{ minWidth: { xs: 80, sm: 150 } }}>
                <RefreshIcon />
                ቅጽ ዳግም አስጀምር
              </Button>
              <Button
                variant="contained"
                disabled={disabled}
                type="submit"
                sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
              >
                {disabled && <CircularProgress style={{ width: '20px', height: '20px' }} />}
                ክፍያ አሳውቅ
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </StyledCardNoHeight>
  );
}

AnnouncePayment.propTypes = {
  user: PropTypes.object.isRequired,
  bankAccounts: PropTypes.object.isRequired,
};
